// FASE 1.5 — Tip button for analysis cards.
// Renders a "💸 PROPINA" button when the post author has a tip_address.
// Clicking opens a modal with the existing TipJarWidget (USDC on Base L2).
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X as XIcon, DollarSign } from "lucide-react";
import { TipJarWidget } from "./TipJarWidget";
import { useLanguage } from "../../i18n/LanguageContext";
import { tFases } from "../../i18n/shadow-fases";

export const TipButton = ({ tipAddress, authorName, postId }) => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  if (!tipAddress) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-amber-200 transition-colors hover:border-amber-400/60 hover:bg-amber-400/[0.12]"
        data-testid={`shadow-card-tip-${postId}`}
        title={tFases(lang, "tip_modal_title", { name: authorName || "" })}
      >
        <DollarSign className="h-3 w-3" /> {tFases(lang, "tip_btn")}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9000] flex items-center justify-center overflow-y-auto bg-black/85 p-3 backdrop-blur-md sm:p-4"
            onClick={() => setOpen(false)}
            data-testid={`shadow-tip-modal-${postId}`}
          >
            <motion.div
              initial={{ scale: 0.92, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 20, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md overflow-hidden rounded-3xl border border-amber-400/30 bg-zinc-950 shadow-[0_0_60px_rgba(245,166,35,0.18)]"
            >
              <button
                onClick={() => setOpen(false)}
                aria-label="close"
                className="absolute right-4 top-4 z-10 rounded-full border border-white/15 bg-black/70 p-1.5 text-white/80 backdrop-blur transition-colors hover:border-white/40 hover:text-white"
                data-testid={`shadow-tip-modal-close-${postId}`}
              >
                <XIcon className="h-4 w-4" />
              </button>
              <div className="max-h-[88vh] overflow-y-auto p-5 pt-7">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/85">
                  {tFases(lang, "tip_modal_eyebrow")}
                </p>
                <h3 className="mt-1 text-lg font-black text-white">
                  {tFases(lang, "tip_modal_title", { name: authorName || "" })}
                </h3>
                <p className="mt-1 text-xs text-white/55">
                  {tFases(lang, "tip_modal_sub")}
                </p>
                <div className="mt-4">
                  <TipJarWidget address={tipAddress} chain="base" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TipButton;
